import { useState, useCallback, useRef } from 'react';
import { uploadMultipartFile, DEFAULT_CHUNK_SIZE } from '../lib/utils/multipart-uploader';

/**
 * Reusable Custom React Hook for Multipart File Uploads
 * Can be used in Posts, Chat Messages, Disk Uploader, etc.
*/
export const useMultipartUpload = (options = {}) => {
  const {
    uploadSource = 'post',
    chunkSize = DEFAULT_CHUNK_SIZE,
    maxConcurrent = 2,
    maxRetries = 3,
    onSingleSuccess,
    onProgress,
    onError,
  } = options;

  // Map of fileId -> { file, uploadId, status, loaded, total, percentage, error }
  const [uploadState, setUploadState] = useState({});
  const uploadersRef = useRef({});

  // Helper to generate a unique key for tracking files
  const getFileId = (file) => `${file.name}_${file.size}_${file.lastModified}`;

  /**
   * Upload a single file using uploadMultipartFile
   */
  const uploadFile = useCallback(
    async (file, source) => {
      const fileId = getFileId(file);
      const effectiveSource = source || uploadSource;

      // Update state for initiating upload
      setUploadState((prev) => ({
        ...prev,
        [fileId]: {
          fileId,
          file,
          uploadId: null,
          status: 'initiating',
          loaded: 0,
          total: file.size,
          percentage: 0,
          error: null,
        },
      }));

      return new Promise((resolve, reject) => {
        const uploader = uploadMultipartFile({
          file,
          uploadSource: effectiveSource,
          chunkSize,
          maxConcurrent,
          maxRetries,
          onStatusChange: (status) => {
            setUploadState((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                status,
              },
            }));
          },
          onProgress: ({ loaded, total, percentage, uploadId }) => {
            setUploadState((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                uploadId: uploadId || prev[fileId]?.uploadId,
                loaded,
                total,
                percentage,
              },
            }));
            if (onProgress) onProgress({ file, loaded, total, percentage, uploadId });
          },
          onError: (err) => {
            setUploadState((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                status: 'error',
                error: err?.message || 'Upload failed',
              },
            }));
            if (onError) onError(err, file);
            reject(err);
          },
          onSuccess: (result) => {
            setUploadState((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                uploadId: result.uploadId,
                status: 'completed',
                percentage: 100,
                loaded: file.size,
              },
            }));
            if (onSingleSuccess) onSingleSuccess(result, file);
            resolve(result);
          },
        });

        uploadersRef.current[fileId] = uploader;
        uploader.start().catch(() => {
          // Handled via onError callback above
        });
      });
    },
    [uploadSource, chunkSize, maxConcurrent, maxRetries, onSingleSuccess, onError]
  );

  /**
   * Upload multiple files in parallel or series
   */
  const uploadFiles = useCallback(
    async (fileList, sourceOverride) => {
      const filesArray = Array.from(fileList || []);
      if (!filesArray.length) return [];

      const uploadPromises = filesArray.map((file) =>
        uploadFile(file, sourceOverride)
      );

      return Promise.allSettled(uploadPromises);
    },
    [uploadFile]
  );

  /**
   * Cancel all active uploads
   */
  const cancelAll = useCallback(() => {
    Object.values(uploadersRef.current).forEach((uploader) => {
      if (uploader && typeof uploader.cancel === 'function') {
        uploader.cancel();
      }
    });
    setUploadState((prev) => {
      const updated = {};
      Object.keys(prev).forEach((id) => {
        updated[id] = { ...prev[id], status: 'cancelled' };
      });
      return updated;
    });
  }, []);

  /**
   * Cancel single file upload
   */
  const cancelUpload = useCallback((file) => {
    if (!file) return;

    const targetFile = file instanceof File ? file : file?.file || file;
    let fileId = typeof targetFile === 'string' ? targetFile : targetFile?.name ? getFileId(targetFile) : null;

    // If fileId isn't directly in uploadersRef, try finding matching key by filename/size
    if (!fileId || !uploadersRef.current[fileId]) {
      const activeKeys = Object.keys(uploadersRef.current);
      if (targetFile?.name) {
        const matchedKey = activeKeys.find((k) => k.startsWith(targetFile.name));
        if (matchedKey) {
          fileId = matchedKey;
        }
      }
    }

    if (fileId && uploadersRef.current[fileId]) {
      const uploader = uploadersRef.current[fileId];
      if (uploader && typeof uploader.cancel === 'function') {
        uploader.cancel();
      }
      setUploadState((prev) => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });
      delete uploadersRef.current[fileId];
    }
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    cancelAll();
    uploadersRef.current = {};
    setUploadState({});
  }, [cancelAll]);

  // Derived state calculations
  const uploadsList = Object.values(uploadState);

  const isUploading = uploadsList.some((item) =>
    ['initiating', 'uploading', 'completing'].includes(item.status)
  );

  const completedUploads = uploadsList.filter(
    (item) => item.status === 'completed' && item.uploadId
  );

  const completedUploadIds = completedUploads.map((item) => item.uploadId);

  return {
    uploadFile,
    uploadFiles,
    cancelUpload,
    cancelAll,
    reset,
    uploadState,
    uploadsList,
    isUploading,
    completedUploads,
    completedUploadIds,
  };
};
